import React, { useState, useEffect } from 'react'
import AccountRow from './AccountRow'
import Tooltip from '../../shared/Tooltip'
import { extractCentsFromDollarString } from '../../../other/money'

const SortIcon = ({ column, currentSort }) => {
  const isActive = currentSort.column === column
  const direction = currentSort.direction

  return (
    <svg
      width='20'
      height='20'
      viewBox='0 0 12 18'
      fill='none'
      xmlns='http://www.w3.org/2000/svg'
      className='ms-1 me-2'
    >
      <path
        d='M9 10.5L6 13.5L3 10.5'
        stroke={isActive && direction === 'desc' ? '#666666' : '#9E9E9E'}
        strokeWidth={isActive && direction === 'desc' ? '2' : '1'}
      />
      <path
        d='M3 7.5L6 4.5L9 7.5'
        stroke={isActive && direction === 'asc' ? '#666666' : '#9E9E9E'}
        strokeWidth={isActive && direction === 'asc' ? '2' : '1'}
      />
    </svg>
  )
}

const DEFAULT_SHOWN_COLUMNS = [
  'Name',
  'Reference',
  'File Number',
  'Amount',
  'Status',
  'Status Date',
  'Offer Amount',
  '# Pays',
  '# Tiers',
  '%',
  'First Payment',
  'Action',
]

const AccountsTable = ({
  accounts,
  setAccounts,
  selectedFilterValues,
  onLoadMore,
  hasMorePages,
  hasPaymentDetails,
  validFirstPaymentDate,
  autoAction,
  lawFirm,
}) => {
  const [activeDropdownId, setActiveDropdownId] = useState(null)
  const [sortConfig, setSortConfig] = React.useState({
    column: null,
    direction: 'asc',
  })

  useEffect(() => {
    const handleClickOutside = event => {
      const dropdowns = document.querySelectorAll('.action-dropdown')
      const buttons = document.querySelectorAll('.action-button')

      if (
        !Array.from(dropdowns).some(dropdown => dropdown.contains(event.target)) &&
        !Array.from(buttons).some(button => button.contains(event.target))
      ) {
        setActiveDropdownId(null)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleSort = column => {
    const direction =
      sortConfig.column === column && sortConfig.direction === 'asc' ? 'desc' : 'asc'
    setSortConfig({ column, direction })

    const actionOrder = [
      'Consumer Offer',
      'Lawyer Sent Confusing Response to Offer',
      'Consumer Counteroffer',
      'Lawyer Counteroffer',
      'Lawyer Accept',
      'Consumer Accept',
      'Lawyer Sent Confusing Response To Payment Details Request',
      'Lawyer Sent Payment Details',
      'Lawyer Sent Settlement Agreement',
      'Consumer Signed Settlement Agreement',
    ]

    const sortedAccounts = [...accounts].sort((a, b) => {
      const compareValue = (val1, val2) => {
        if (val1 < val2) return -1
        if (val1 > val2) return 1
        return 0
      }

      // Special compare for strings that might be empty/null/whitespace
      const compareStringWithEmpty = (val1, val2, emptyLast = false) => {
        const isEmpty1 = val1 === null || val1 === undefined || val1.trim() === ''
        const isEmpty2 = val2 === null || val2 === undefined || val2.trim() === ''

        if (isEmpty1 && isEmpty2) return 0
        if (isEmpty1) return emptyLast ? 1 : -1
        if (isEmpty2) return emptyLast ? -1 : 1

        return compareValue(val1, val2)
      }

      let comparison = 0
      switch (column) {
        case 'id':
          comparison = compareValue(a.id, b.id)
          break
        case 'name':
          comparison = compareStringWithEmpty(
            a.customer_name?.toLowerCase(),
            b.customer_name?.toLowerCase()
          )
          break
        case 'lawFirm':
          comparison = compareStringWithEmpty(a.law_firm_name, b.law_firm_name)
          break
        case 'birthdate':
          comparison = compareValue(new Date(a.birthdate), new Date(b.birthdate))
          break
        case 'reference':
          comparison = compareStringWithEmpty(
            a.civil_number?.toLowerCase(),
            b.civil_number?.toLowerCase()
          )
          break
        case 'fileNumber':
          // Display empty file numbers last when sorted
          comparison = compareStringWithEmpty(
            a.file_number?.toLowerCase(),
            b.file_number?.toLowerCase(),
            true // This flag ensures empty values appear at the end
          )
          break
        case 'lawsuitState':
          comparison = compareStringWithEmpty(
            a.lawsuit_state?.toLowerCase(),
            b.lawsuit_state?.toLowerCase()
          )
          break
        case 'plaintiff':
          comparison = compareStringWithEmpty(
            a.plaintiff_name?.toLowerCase(),
            b.plaintiff_name?.toLowerCase()
          )
          break
        case 'amount':
          comparison = compareValue(
            parseFloat(a.lawsuit_amount?.replace(/[$,]/g, '')) || 0,
            parseFloat(b.lawsuit_amount?.replace(/[$,]/g, '')) || 0
          )
          break
        case 'status':
          comparison = compareValue(
            actionOrder.indexOf(a.last_action),
            actionOrder.indexOf(b.last_action)
          )
          break
        case 'statusDate':
          comparison = compareValue(
            new Date(a.latest_event.created_at),
            new Date(b.latest_event.created_at)
          )
          break
        case 'offerAmount':
          comparison = compareValue(
            parseFloat(a.latest_historical_offer?.total_amount_cents) || 0,
            parseFloat(b.latest_historical_offer?.total_amount_cents) || 0
          )
          break
        case 'numberOfPayments':
          comparison = compareValue(
            parseInt(a.latest_historical_offer?.payments) || 0,
            parseInt(b.latest_historical_offer?.payments) || 0
          )
          break
        case 'numberOfTiers':
          comparison = compareValue(
            parseInt(a.latest_historical_offer?.phases) || 0,
            parseInt(b.latest_historical_offer?.phases) || 0
          )
          break
        case 'percentage':
          comparison = compareValue(
            a.latest_historical_offer?.total_amount_cents && a.lawsuit_amount
              ? Math.round(
                  (a.latest_historical_offer.total_amount_cents /
                    extractCentsFromDollarString(a.lawsuit_amount)) *
                    100
                )
              : 0,
            b.latest_historical_offer?.total_amount_cents && b.lawsuit_amount
              ? Math.round(
                  (b.latest_historical_offer.total_amount_cents /
                    extractCentsFromDollarString(b.lawsuit_amount)) *
                    100
                )
              : 0
          )
          break
        case 'firstPayment':
          comparison = compareValue(
            new Date(a.latest_historical_offer?.first_payment_date),
            new Date(b.latest_historical_offer?.first_payment_date)
          )
          break
        default:
          comparison = 0
      }

      return direction === 'asc' ? comparison : -comparison
    })

    setAccounts(sortedAccounts)
  }

  const columnsToShow = selectedFilterValues.columns || DEFAULT_SHOWN_COLUMNS

  return (
    <>
      <div
        id='table-container'
        className='table-responsive'
        style={{ position: 'relative', minHeight: '500px' }}
      >
        <table className='table table-sm' style={{ verticalAlign: 'middle' }}>
          <thead>
            <tr>
              {!lawFirm && (
                <th
                  scope='col'
                  className='align-middle px-2'
                  onClick={() => handleSort('id')}
                  style={{ cursor: 'pointer' }}
                >
                  <div className='d-flex align-items-center'>
                    ID <SortIcon column='id' currentSort={sortConfig} />
                  </div>
                </th>
              )}
              {columnsToShow.includes('Name') && (
                <th
                  scope='col'
                  className='align-middle px-2 sticky-col'
                  onClick={() => handleSort('name')}
                  style={{
                    cursor: 'pointer',
                    left: 0,
                    position: 'sticky',
                    backgroundColor: 'white',
                    zIndex: 2,
                  }}
                >
                  <Tooltip text='Consumer name' position='bottom'>
                    <div className='d-flex align-items-center'>
                      Name <SortIcon column='name' currentSort={sortConfig} />
                    </div>
                  </Tooltip>
                </th>
              )}
              {columnsToShow.includes('Law Firm') && (
                <th
                  scope='col'
                  className='align-middle px-2'
                  onClick={() => handleSort('lawFirm')}
                  style={{ cursor: 'pointer' }}
                >
                  <div className='d-flex align-items-center justify-content-center'>
                    Law Firm <SortIcon column='lawFirm' currentSort={sortConfig} />
                  </div>
                </th>
              )}
              {columnsToShow.includes('Birthdate') && (
                <th
                  scope='col'
                  className='align-middle px-2'
                  onClick={() => handleSort('birthdate')}
                  style={{ cursor: 'pointer' }}
                >
                  <Tooltip text="Consumer's date of birth" position='bottom'>
                    <div
                      className='d-flex align-items-center justify-content-center'
                      style={{ minWidth: '100px' }}
                    >
                      Birthdate <SortIcon column='birthdate' currentSort={sortConfig} />
                    </div>
                  </Tooltip>
                </th>
              )}
              {columnsToShow.includes('Reference') && (
                <th
                  scope='col'
                  className='align-middle px-2'
                  onClick={() => handleSort('reference')}
                  style={{ cursor: 'pointer' }}
                >
                  <Tooltip text='Civil case number' position='bottom'>
                    <div
                      className='d-flex align-items-center justify-content-center'
                      style={{ minWidth: '100px' }}
                    >
                      Reference <SortIcon column='reference' currentSort={sortConfig} />
                    </div>
                  </Tooltip>
                </th>
              )}
              {columnsToShow.includes('File Number') && (
                <th
                  scope='col'
                  className='align-middle px-2'
                  onClick={() => handleSort('fileNumber')}
                  style={{ cursor: 'pointer' }}
                >
                  <Tooltip text='Internal file/account number' position='bottom'>
                    <div
                      className='d-flex align-items-center justify-content-center'
                      style={{ minWidth: '100px' }}
                    >
                      File Number <SortIcon column='fileNumber' currentSort={sortConfig} />
                    </div>
                  </Tooltip>
                </th>
              )}
              {columnsToShow.includes('Lawsuit State') && (
                <th
                  scope='col'
                  className='align-middle px-2'
                  onClick={() => handleSort('lawsuitState')}
                  style={{ cursor: 'pointer' }}
                >
                  <Tooltip text='State where lawsuit was filed' position='bottom'>
                    <div
                      className='d-flex align-items-center justify-content-center'
                      style={{ minWidth: '80px' }}
                    >
                      State <SortIcon column='lawsuitState' currentSort={sortConfig} />
                    </div>
                  </Tooltip>
                </th>
              )}
              {columnsToShow.includes('Plaintiff') && (
                <th
                  scope='col'
                  className='align-middle px-2'
                  onClick={() => handleSort('plaintiff')}
                  style={{ cursor: 'pointer' }}
                >
                  <div className='d-flex align-items-center justify-content-center'>
                    Plaintiff <SortIcon column='plaintiff' currentSort={sortConfig} />
                  </div>
                </th>
              )}
              {columnsToShow.includes('Amount') && (
                <th
                  scope='col'
                  className='align-middle px-2'
                  onClick={() => handleSort('amount')}
                  style={{ cursor: 'pointer' }}
                >
                  <Tooltip text='The amount of the debt' position='bottom'>
                    <div
                      className='d-flex align-items-center justify-content-center'
                      style={{ minWidth: '100px' }}
                    >
                      Amount <SortIcon column='amount' currentSort={sortConfig} />
                    </div>
                  </Tooltip>
                </th>
              )}
              {columnsToShow.includes('Status') && (
                <th
                  scope='col'
                  className='align-middle px-2 text-center'
                  onClick={() => handleSort('status')}
                  style={{ cursor: 'pointer' }}
                >
                  <Tooltip text='The last action taken on the account' position='bottom'>
                    <div
                      className='d-flex align-items-center justify-content-center'
                      style={{ minWidth: '110px' }}
                    >
                      Status <SortIcon column='status' currentSort={sortConfig} />
                    </div>
                  </Tooltip>
                </th>
              )}
              {columnsToShow.includes('Status Date') && (
                <th
                  scope='col'
                  className='align-middle px-2'
                  onClick={() => handleSort('statusDate')}
                  style={{ cursor: 'pointer' }}
                >
                  <Tooltip
                    width='280px'
                    text='The date of the last action taken on the account'
                    position='bottom'
                  >
                    <div
                      className='d-flex align-items-center justify-content-center'
                      style={{ minWidth: '100px' }}
                    >
                      Status Date <SortIcon column='statusDate' currentSort={sortConfig} />
                    </div>
                  </Tooltip>
                </th>
              )}
              {columnsToShow.includes('Offer Amount') && (
                <th
                  scope='col'
                  className='align-middle px-2'
                  onClick={() => handleSort('offerAmount')}
                  style={{ cursor: 'pointer' }}
                >
                  <Tooltip
                    width='280px'
                    text='The settlement amount of the last action'
                    position='bottom'
                  >
                    <div
                      className='d-flex align-items-center justify-content-center'
                      style={{ minWidth: '100px' }}
                    >
                      Offer Amount <SortIcon column='offerAmount' currentSort={sortConfig} />
                    </div>
                  </Tooltip>
                </th>
              )}
              {columnsToShow.includes('# Pays') && (
                <th
                  scope='col'
                  className='align-middle px-2'
                  onClick={() => handleSort('numberOfPayments')}
                  style={{ cursor: 'pointer' }}
                >
                  <Tooltip
                    width='280px'
                    text='The number of payments in the settlement'
                    position='bottom'
                  >
                    <div
                      className='d-flex align-items-center justify-content-center'
                      style={{ minWidth: '80px' }}
                    >
                      # Pays <SortIcon column='numberOfPayments' currentSort={sortConfig} />
                    </div>
                  </Tooltip>
                </th>
              )}
              {columnsToShow.includes('# Tiers') && (
                <th
                  scope='col'
                  className='align-middle px-2'
                  onClick={() => handleSort('numberOfTiers')}
                  style={{ cursor: 'pointer' }}
                >
                  <div
                    className='d-flex align-items-center justify-content-center'
                    style={{ minWidth: '80px' }}
                  >
                    # Tiers <SortIcon column='numberOfTiers' currentSort={sortConfig} />
                  </div>
                </th>
              )}
              {columnsToShow.includes('%') && (
                <th
                  scope='col'
                  className='align-middle px-2'
                  onClick={() => handleSort('percentage')}
                  style={{ cursor: 'pointer' }}
                >
                  <Tooltip
                    width='280px'
                    text='The offer amount divided by the debt amount'
                    position='bottom'
                  >
                    <div
                      className='d-flex align-items-center justify-content-center'
                      style={{ minWidth: '80px' }}
                    >
                      % <SortIcon column='percentage' currentSort={sortConfig} />
                    </div>
                  </Tooltip>
                </th>
              )}
              {columnsToShow.includes('First Payment') && (
                <th
                  scope='col'
                  className='align-middle px-2'
                  onClick={() => handleSort('firstPayment')}
                  style={{ cursor: 'pointer' }}
                >
                  <Tooltip
                    width='280px'
                    text='The date of the of the first payment in the settlement'
                    position='bottom'
                  >
                    <div
                      className='d-flex align-items-center justify-content-center'
                      style={{ minWidth: '100px' }}
                    >
                      First Payment <SortIcon column='firstPayment' currentSort={sortConfig} />
                    </div>
                  </Tooltip>
                </th>
              )}
              <th scope='col'></th>
              <th
                scope='col'
                className='align-middle px-2'
                onClick={() => handleSort('status')}
                style={{ cursor: 'pointer' }}
              >
                <Tooltip text='Current action being taken' position='bottom'>
                  <div
                    className='d-flex align-items-center justify-content-center'
                    style={{ minWidth: '80px' }}
                  >
                    Action <SortIcon column='status' currentSort={sortConfig} />
                  </div>
                </Tooltip>
              </th>
            </tr>
          </thead>
          <tbody className='small'>
            {accounts.map(account => (
              <AccountRow
                lawFirm={lawFirm}
                key={account.id}
                selectedFilterValues={selectedFilterValues}
                validFirstPaymentDate={validFirstPaymentDate}
                hasPaymentDetails={hasPaymentDetails}
                account={account}
                autoAction={autoAction}
                columnsToShow={columnsToShow}
                activeDropdownId={activeDropdownId}
                setActiveDropdownId={setActiveDropdownId}
              />
            ))}
          </tbody>
        </table>

        {hasMorePages && (
          <div className='text-center mt-4'>
            <button onClick={onLoadMore} className='btn btn-primary'>
              Load More
            </button>
          </div>
        )}
      </div>
    </>
  )
}

export default AccountsTable
